import React, { ChangeEvent, FormEvent, useEffect, useReducer, useState } from 'react';
import {
	sealCategoryList,
	sealSpecsCategoryData,
	sealSpecsColumnLabels,
	sealSpecsLabels,
	sealSpecsRequiredKeys,
	text,
	unitMap,
} from '../../data';
import specificationReducer, {
	SealSpecsFilter,
	SpecificationSearchInputKey,
	initialSpecificationState,
	isSealKeyApplicable,
} from './reducer';
import {
	getSealDetailsUrl,
	getSealBySpecs,
	getSealMaterialType,
	sealSearchContent,
	SELECTION_ANY,
	getSealDetailsDrawUrl,
	transformSealData
} from '../../../components/bearing-search/api/api';
import SelectDropdown from '../../../components/bearing-search/components/forms/SelectDropdown';
import {
	SealSearchCategoryDetails,
	SealSortPayload,
	SealSpecificationPayload,
	SealSpecificationResponse,
	TimkenUnitDimension,
} from '../../../components/bearing-search/types/types';
import { SealCategory, SelectOption } from '../../types/types';
import NumberInput from '../../../components/bearing-search/components/forms/NumberInput';
import { generateId, getTooltip, toggleNextTools } from '../../utils/engineering-tools';
import SealSearchResults from '../../components/seal-search-results/SealSearchResults';
import { getDimensionOptions } from './options';
import { trackProductResults, updateActionParams } from '../../utils/analytics';

const SpecificationSearch: React.FC = () => {
	const formDescription = sealSearchContent.specificationDesc
		? { __html: sealSearchContent.specificationDesc }
		: undefined;
	const [ sealMaterials, setSealMaterials ] = useState<SelectOption<string>[]>( [] );
	const [ sealTypes, setSealTypes ] = useState<SelectOption<string>[]>( [] );
	const [ resultsKeys, setResultsKeys ] = useState<string[]>( [] );
	const [ isSealMaterialTypeLoading, setIsSealMaterialTypeLoading ] = useState( false );
	const [ isSearched, setIsSearched ] = useState( false );
	const [ isValidated, setIsValidated ] = useState( false );
	const [ results, setResults ] = useState<string[][]>( [] );
	const [ isResultsLoading, setIsResultsLoading ] = useState( false );
	const [ isRefinementOnly, setIsRefinementOnly ] = useState( false );
	const [ formState, dispatch ] = useReducer( specificationReducer, initialSpecificationState );

	useEffect( () => {
		const { category: sealCategory, unitDimension: unit } = formState;

		if ( isSealKeyApplicable( sealCategory, 'type' ) || isSealKeyApplicable( sealCategory, 'material' ) ) {
			setIsSealMaterialTypeLoading( true );

			getSealMaterialType( { sealCategory, unit } )
				.then( ( response ) => {
					const materialList = response.materialData
						.filter( ( material ) => !! material )
						.map( ( material ) => {
							return {
								id: `seal_material_${ generateId( material ) }`,
								value: material,
								label: material,
							} as SelectOption<string>;
						} );

					const typeList = response.typeData
						.filter( ( type ) => !! type )
						.map( ( type ) => {
							return {
								id: `seal_type_${ generateId( type ) }`,
								value: type,
								label: type,
							} as SelectOption<string>;
						} );

					setSealMaterials( [
						{
							id: 'seal_material_any',
							value: SELECTION_ANY,
							label: text.any,
						},
						...materialList,
					] );
					setSealTypes( [
						{
							id: 'seal_type_any',
							value: SELECTION_ANY,
							label: text.any,
						},
						...typeList,
					] );

					dispatch( {
						type: 'update_request_data',
					} );
				} )
				.finally( () => {
					setIsSealMaterialTypeLoading( false );
				} );
		}
	}, [ formState.category, formState.unitDimension ] );

	useEffect( () => {
		const isShown = isSearched && ! isResultsLoading;
		toggleNextTools( isShown );

		return () => toggleNextTools( false );
	}, [ isSearched, isResultsLoading ] );

	const handleFormChange = () => {
		setIsValidated( false );

		dispatch( {
			type: 'update_request_data',
		} );
	};

	const handleSubmit = ( event: FormEvent ) => {
		event.preventDefault();

		setIsValidated( true );

		if ( formState.requestData ) {
			const request: SealSpecificationPayload = formState.requestData;

			setIsSearched( true );
			setIsResultsLoading( true );
			setResultsKeys( sealSpecsCategoryData[ formState.category ].responseFields[ formState.unitDimension ] as string[] );

			setIsRefinementOnly( false );

			getSealBySpecs( request )
				.then( ( response ) => {
					const sealResults = transformSealData( response, formState.unitDimension );
					trackProductResults( 'sealSearch', request, sealResults.length );
					updateActionParams( 'sealSearch' );
					setResults( sealResults );

					document.querySelector('.t-engineering-tools__results').scrollIntoView({
						behavior: 'smooth'
					});

				} )
				.finally( () => setIsResultsLoading( false ) );
		}
	};

	const handleSort = ( sortOptions?: SealSortPayload ) => {
		if ( ! formState.requestData ) {
			return;
		}

		const request: SealSpecificationPayload = {
			...formState.requestData,
			...sortOptions,
		};

		setIsRefinementOnly( true );

		getSealBySpecs( request )
			.then( ( response ) => {
				setResults( transformSealData( response, formState.unitDimension ) );
			} )
			.finally( () => setIsResultsLoading( false ) );
	};

	const clearSearch = () => {
		setIsSearched( false );

		dispatch( {
			type: 'reset',
		} );

		dispatch( {
			type: 'change_seal_category',
			payload: initialSpecificationState.category,
		} );
	};

	return (
		<div>
			<form onSubmit={handleSubmit} onChange={handleFormChange}>
				{!! formDescription && (
					<p className="t-engineering-tools__description" dangerouslySetInnerHTML={formDescription}></p>
				)}

				<p>
					<span className="c-form__asterisk">*</span>
					{text.requiredMessage}
				</p>

				<div>
					<div className="row">
						<div className="col-md-3">
							<SelectDropdown
								id="seal_specs_category"
								name="category"
								required={true}
								label={sealSpecsLabels.category}
								tooltip={getTooltip( sealSpecsLabels.category )}
								value={formState.category}
								options={sealCategoryList}
								onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
									dispatch( {
										type: 'change_seal_category',
										payload: event.target.value as SealCategory,
									} )
								}
							/>
						</div>
						<div className="col-md-2">
							<SelectDropdown
								id="seal_specs_unit"
								name="unitDimension"
								required={true}
								label={sealSpecsLabels.unitDimension}
								value={formState.unitDimension}
								options={getDimensionOptions( formState.category )}
								onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
									dispatch( {
										type: 'change_unit',
										payload: event.target.value as TimkenUnitDimension,
									} )
								}
							/>
						</div>
					</div>

					<div className="row">
						{Object.keys( formState.specsFilters )
							.filter( ( key ) => key !== 'crossSectionHeight' && key !== 'overallWidth' )
							.map( ( filterKey ) => {
								const key = filterKey as SpecificationSearchInputKey;
								const filter = formState.specsFilters[ key ];

								if ( ! filter?.isApplicable ) {
									return null;
								}

								const { id, label, value, type, tooltip } = filter as SealSpecsFilter;
								const required = sealSpecsRequiredKeys.includes( key );
								const validation = formState.validation.get( key );
								const unitLabel = unitMap[ formState.unitDimension === 'Metric' ? 'M' : 'I' ][ type ];

								return (
									<div className="col-md-3" key={id}>
										<NumberInput
											id={id}
											label={label}
											unitLabel={unitLabel}
											tooltip={tooltip}
											value={value}
											min={0}
											required={required}
											invalid={isValidated && validation?.invalid}
											errorMessage={validation?.message}
											onChange={( event: ChangeEvent<HTMLInputElement> ) =>
												dispatch( {
													type: 'change_number_field',
													payload: [ key, event.target.value ],
												} )
											}
										/>
									</div>
								);
							} )}

						{isSealKeyApplicable( formState.category, 'material' ) && (
							<div className="col-md-3">
								<SelectDropdown
									id="seal_specs_material"
									name="material"
									label={sealSpecsLabels.material || ''}
									value={formState.material}
									options={sealMaterials}
									loading={isSealMaterialTypeLoading}
									onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
										dispatch( {
											type: 'change_material',
											payload: event.target.value,
										} )
									}
								/>
							</div>
						)}

						{isSealKeyApplicable( formState.category, 'type' ) && (
							<div className="col-md-3">
								<SelectDropdown
									id="seal_specs_type"
									name="type"
									label={sealSpecsLabels.type || ''}
									value={formState.type}
									options={sealTypes}
									loading={isSealMaterialTypeLoading}
									onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
										dispatch( {
											type: 'change_type',
											payload: event.target.value,
										} )
									}
								/>
							</div>
						)}

						{Object.keys( formState.specsFilters )
							.filter( ( key ) => key === 'crossSectionHeight' || key === 'overallWidth' )
							.map( ( filterKey ) => {
								const key = filterKey as SpecificationSearchInputKey;
								const filter = formState.specsFilters[ key ];

								if ( ! filter?.isApplicable ) {
									return null;
								}

								const { id, label, value, type, tooltip } = filter as SealSpecsFilter;
								const required = sealSpecsRequiredKeys.includes( key );
								const validation = formState.validation.get( key );
								const unitLabel = unitMap[ formState.unitDimension === 'Metric' ? 'M' : 'I' ][ type ];

								return (
									<div className="col-md-3" key={id}>
										<NumberInput
											id={id}
											label={label}
											unitLabel={unitLabel}
											tooltip={tooltip}
											value={value}
											min={0}
											required={required}
											invalid={isValidated && validation?.invalid}
											errorMessage={validation?.message}
											onChange={( event: ChangeEvent<HTMLInputElement> ) =>
												dispatch( {
													type: 'change_number_field',
													payload: [ key, event.target.value ],
												} )
											}
										/>
									</div>
								);
							} )}
					</div>
				</div>

				<div className="t-engineering-tools__search-actions">
					<button className="btn btn--primary" type="submit" disabled={isSealMaterialTypeLoading || isValidated}>
						{text.search}
					</button>

					<button className="btn btn--secondary" type="button" onClick={() => clearSearch()}>
						{text.clear}
					</button>
				</div>
			</form>

			{isSearched && (
				<SealSearchResults
					data={results}
					refining={isRefinementOnly}
					columnKeys={resultsKeys}
					columnLabels={sealSpecsColumnLabels}
					loading={isResultsLoading}
					urlComposer={getSealDetailsUrl}
					drawUrlComposer={getSealDetailsDrawUrl}
					onSort={handleSort}
					unit={formState.unitDimension}
				/>
			)}
		</div>
	);
};

export default SpecificationSearch;
